import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle, MapPin, CalendarBlank, Trophy, ArrowRight } from "@phosphor-icons/react";

const empty = { opponent: "", match_date: "", match_time: "", location: "", competition: "", uniform_color: "", notes: "", completed: false, home_score: "", away_score: "" };

export default function Matches() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);

  async function reload() { const r = await api.get("/matches"); setItems(r.data); }
  useEffect(() => { reload(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = { ...form, home_score: form.home_score === "" ? null : +form.home_score, away_score: form.away_score === "" ? null : +form.away_score };
      await api.post("/matches", payload);
      toast.success("Match created");
      setForm(empty); setShowForm(false); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function toggleCompleted(m) {
    await api.put(`/matches/${m.id}`, { ...m, completed: !m.completed });
    reload();
  }

  async function remove(id) {
    if (!window.confirm("Delete this match?")) return;
    await api.delete(`/matches/${id}`); reload();
  }

  const inputCls = "w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]";

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Fixtures</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Matches</h1>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="add-match-btn"
          className="h-14 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider inline-flex items-center gap-2"
        >
          <Plus size={22} weight="bold" /> New Match
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#121212] border border-neutral-800 rounded-lg p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <input required data-testid="match-opponent" placeholder="Opponent *" className={inputCls} value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} />
          <input required type="date" className={inputCls} value={form.match_date} onChange={(e) => setForm({ ...form, match_date: e.target.value })} />
          <input type="time" className={inputCls} value={form.match_time} onChange={(e) => setForm({ ...form, match_time: e.target.value })} />
          <input placeholder="Location" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <input placeholder="Competition" className={inputCls} value={form.competition} onChange={(e) => setForm({ ...form, competition: e.target.value })} />
          <input placeholder="Uniform Color" className={inputCls} value={form.uniform_color} onChange={(e) => setForm({ ...form, uniform_color: e.target.value })} />
          <div className="md:col-span-3">
            <textarea rows={2} placeholder="Notes" className="w-full p-3 bg-[#0a0a0a] border border-neutral-800 rounded-md" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <button type="submit" data-testid="submit-match-btn" className="md:col-span-3 h-12 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider">Save</button>
        </form>
      )}

      {items.length === 0 ? (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-10 text-center overline">No matches yet</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((m) => (
            <div key={m.id} className="bg-[#121212] border border-neutral-800 rounded-lg p-5 card-lift">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="font-display font-black text-2xl uppercase tracking-tight">vs {m.opponent}</div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-neutral-400">
                    <span className="flex items-center gap-1"><CalendarBlank size={14} weight="bold" /> {m.match_date} {m.match_time}</span>
                    {m.location && <span className="flex items-center gap-1"><MapPin size={14} weight="bold" /> {m.location}</span>}
                    {m.competition && <span className="flex items-center gap-1"><Trophy size={14} weight="bold" /> {m.competition}</span>}
                  </div>
                </div>
                {m.completed ? (
                  <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded bg-[#06D6A0] text-[#050505]">Done</span>
                ) : (
                  <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded bg-[#FFD166] text-[#050505]">Upcoming</span>
                )}
              </div>
              {(m.home_score !== null && m.home_score !== undefined && m.away_score !== null && m.away_score !== undefined) && (
                <div className="mt-3 font-display font-black text-3xl">{m.home_score} - {m.away_score}</div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={`/matches/${m.id}/stats`}
                  data-testid={`match-stats-${m.id}`}
                  className="h-11 px-4 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
                >
                  Record Stats <ArrowRight size={14} weight="bold" />
                </Link>
                <button
                  onClick={() => toggleCompleted(m)}
                  className="h-11 px-4 rounded-md border border-neutral-800 hover:border-[#06D6A0] text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2"
                >
                  <CheckCircle size={14} weight="bold" /> {m.completed ? "Reopen" : "Mark Done"}
                </button>
                <button onClick={() => remove(m.id)} className="h-11 px-3 rounded-md border border-neutral-800 hover:border-[#D90429] text-[#D90429]"><Trash size={14} weight="bold" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
